import { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { Rights, useProfileQuery } from '../../graphql/types';
import { useAiChat, UseAiChatReturn } from '../../hooks/useAiChat';
import AiChatPopup from './AiChatPopup';

interface AiChatContextValue {
  available: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
  toggle: () => void;
  chat: UseAiChatReturn | null;
}

const AiChatContext = createContext<AiChatContextValue | null>(null);

const AvailableAiChatProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const chat = useAiChat();

  const value = useMemo<AiChatContextValue>(() => ({
    available: true,
    open,
    setOpen,
    toggle: () => setOpen(prev => !prev),
    chat,
  }), [open, chat]);

  return (
    <AiChatContext.Provider value={value}>
      {children}
      <AiChatPopup open={open} onClose={() => setOpen(false)} chat={chat} />
    </AiChatContext.Provider>
  );
};

const UnavailableAiChatProvider = ({ children }: { children: ReactNode }) => {
  const value = useMemo<AiChatContextValue>(() => ({
    available: false,
    open: false,
    setOpen: () => {},
    toggle: () => {},
    chat: null,
  }), []);

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
};

export const AI_CHAT_ALLOWED_RIGHTS = [Rights.IsSuperAdmin, Rights.IsPreUnderwriter];

export const AiChatProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useProfileQuery({ fetchPolicy: 'cache-first' });
  const rights = data?.profile?.rights ?? [];
  const hasAccess = AI_CHAT_ALLOWED_RIGHTS.some((r) => rights.includes(r));

  if (!hasAccess) {
    return <UnavailableAiChatProvider>{children}</UnavailableAiChatProvider>;
  }
  return <AvailableAiChatProvider>{children}</AvailableAiChatProvider>;
};

export const useAiChatContext = (): AiChatContextValue => {
  const ctx = useContext(AiChatContext);
  if (!ctx) {
    return {
      available: false,
      open: false,
      setOpen: () => {},
      toggle: () => {},
      chat: null,
    };
  }
  return ctx;
};
